// TERRA – mock data for executive demo
// All types aligned with src/types/index.ts
import type {
  Alert,
  AlertLevel,
  AlertStatus,
  ModelInfo,
  ModelStatus,
  RiskCategory,
} from '@/types';

// --------------------------------------------------
// Constants
// --------------------------------------------------
export const ITALY_CENTER = { lat: 42.5, lon: 12.5 };
export const NISCEMI_CENTER = { lat: 37.1498, lon: 14.3896 };

// PAI risk levels: P1 = low, P2 = moderate, P3 = high, P4 = critical
export const ALERT_COLORS: Record<AlertLevel, string> = {
  P1: '#10b981', // green  – low
  P2: '#f59e0b', // amber  – moderate
  P3: '#f97316', // orange – high
  P4: '#ef4444', // red    – critical
};

export const ALERT_BG: Record<AlertLevel, string> = {
  P1: 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400',
  P2: 'bg-amber-500/20 border-amber-500/40 text-amber-400',
  P3: 'bg-orange-500/20 border-orange-500/40 text-orange-400',
  P4: 'bg-red-500/20 border-red-500/40 text-red-400',
};

export const ALERT_LEVEL_LABELS: Record<AlertLevel, string> = {
  P1: 'Basso',
  P2: 'Moderato',
  P3: 'Elevato',
  P4: 'Critico',
};

// --------------------------------------------------
// Monitored areas – lightweight structures for map markers
// (not matching a canonical type, used only internally)
// --------------------------------------------------
export interface MonitoredArea {
  id: string;
  name: string;
  region: string;
  province: string;
  lat: number;
  lon: number;
  ewScore: number;
  level: AlertLevel;
  population: number;
  areaKm2: number;
  lastUpdate: string;
}

export const MONITORED_AREAS: MonitoredArea[] = [
  { id: 'NIS001', name: 'Niscemi', region: 'Sicilia', province: 'Caltanissetta', lat: 37.1498, lon: 14.3896, ewScore: 8.7, level: 'P4', population: 26000, areaKm2: 213, lastUpdate: '2026-03-08T06:30:00Z' },
  { id: 'MOD001', name: 'Modena Pianura', region: 'Emilia-Romagna', province: 'Modena', lat: 44.6471, lon: 10.9252, ewScore: 7.2, level: 'P3', population: 185000, areaKm2: 842, lastUpdate: '2026-03-08T06:15:00Z' },
  { id: 'ARG001', name: 'Argenta – Reno', region: 'Emilia-Romagna', province: 'Ferrara', lat: 44.6134, lon: 11.8296, ewScore: 7.8, level: 'P3', population: 22000, areaKm2: 318, lastUpdate: '2026-03-08T06:15:00Z' },
  { id: 'TRE001', name: 'Trento Valsugana', region: 'Trentino-A.A.', province: 'Trento', lat: 46.0748, lon: 11.1217, ewScore: 5.4, level: 'P2', population: 116000, areaKm2: 157, lastUpdate: '2026-03-08T05:00:00Z' },
  { id: 'GEN001', name: 'Genova Bisagno', region: 'Liguria', province: 'Genova', lat: 44.4056, lon: 8.9463, ewScore: 6.1, level: 'P2', population: 580000, areaKm2: 243, lastUpdate: '2026-03-08T05:30:00Z' },
  { id: 'CAL001', name: 'Catanzaro Coste', region: 'Calabria', province: 'Catanzaro', lat: 38.9098, lon: 16.5877, ewScore: 4.3, level: 'P2', population: 89000, areaKm2: 112, lastUpdate: '2026-03-08T04:00:00Z' },
  { id: 'ROM001', name: 'Roma Tevere Sud', region: 'Lazio', province: 'Roma', lat: 41.7005, lon: 12.4843, ewScore: 3.2, level: 'P1', population: 420000, areaKm2: 580, lastUpdate: '2026-03-08T03:00:00Z' },
  { id: 'FIR001', name: 'Firenze Arno', region: 'Toscana', province: 'Firenze', lat: 43.7696, lon: 11.2558, ewScore: 3.8, level: 'P1', population: 367000, areaKm2: 102, lastUpdate: '2026-03-08T03:30:00Z' },
  { id: 'PAL001', name: 'Palermo Oreto', region: 'Sicilia', province: 'Palermo', lat: 38.1157, lon: 13.3615, ewScore: 4.9, level: 'P2', population: 674000, areaKm2: 158, lastUpdate: '2026-03-08T05:00:00Z' },
  { id: 'NAP001', name: 'Napoli Flegrei', region: 'Campania', province: 'Napoli', lat: 40.8518, lon: 14.2681, ewScore: 5.8, level: 'P2', population: 962000, areaKm2: 117, lastUpdate: '2026-03-08T05:45:00Z' },
  { id: 'TOR001', name: 'Torino Po Valle', region: 'Piemonte', province: 'Torino', lat: 45.0703, lon: 7.6869, ewScore: 2.1, level: 'P1', population: 875000, areaKm2: 130, lastUpdate: '2026-03-08T02:00:00Z' },
  { id: 'BAR001', name: 'Bari Puglia Nord', region: 'Puglia', province: 'Bari', lat: 41.1171, lon: 16.8719, ewScore: 2.4, level: 'P1', population: 320000, areaKm2: 116, lastUpdate: '2026-03-08T02:30:00Z' },
  { id: 'VEN001', name: 'Venezia Mestre', region: 'Veneto', province: 'Venezia', lat: 45.4408, lon: 12.3155, ewScore: 4.1, level: 'P2', population: 258000, areaKm2: 415, lastUpdate: '2026-03-08T04:30:00Z' },
  { id: 'BOL001', name: 'Bologna Reno', region: 'Emilia-Romagna', province: 'Bologna', lat: 44.4949, lon: 11.3426, ewScore: 5.9, level: 'P2', population: 416000, areaKm2: 140, lastUpdate: '2026-03-08T05:15:00Z' },
  { id: 'ANC001', name: 'Ancona Marche', region: 'Marche', province: 'Ancona', lat: 43.6158, lon: 13.5189, ewScore: 6.4, level: 'P3', population: 100000, areaKm2: 123, lastUpdate: '2026-03-08T06:00:00Z' },
  { id: 'REG001', name: 'Reggio Calabria', region: 'Calabria', province: 'Reggio Calabria', lat: 38.1110, lon: 15.6598, ewScore: 5.1, level: 'P2', population: 180000, areaKm2: 239, lastUpdate: '2026-03-08T05:00:00Z' },
  { id: 'CAG001', name: 'Cagliari Campidano', region: 'Sardegna', province: 'Cagliari', lat: 39.2238, lon: 9.1217, ewScore: 3.5, level: 'P1', population: 155000, areaKm2: 85, lastUpdate: '2026-03-08T03:00:00Z' },
  { id: 'LAQ001', name: "L'Aquila Gran Sasso", region: 'Abruzzo', province: "L'Aquila", lat: 42.3498, lon: 13.3995, ewScore: 4.6, level: 'P2', population: 70000, areaKm2: 473, lastUpdate: '2026-03-08T04:45:00Z' },
  { id: 'COS001', name: 'Cosenza Crati', region: 'Calabria', province: 'Cosenza', lat: 39.3000, lon: 16.2500, ewScore: 5.3, level: 'P2', population: 68000, areaKm2: 37, lastUpdate: '2026-03-08T05:00:00Z' },
  { id: 'VER001', name: 'Verona Adige', region: 'Veneto', province: 'Verona', lat: 45.4384, lon: 10.9916, ewScore: 2.8, level: 'P1', population: 259000, areaKm2: 198, lastUpdate: '2026-03-08T03:00:00Z' },
];

// --------------------------------------------------
// Alerts – aligned with canonical Alert interface
// --------------------------------------------------
const makeAlert = (
  id: string,
  title: string,
  description: string,
  level: AlertLevel,
  category: RiskCategory,
  status: AlertStatus,
  municipality: string,
  province: string,
  region: string,
  lat: number,
  lon: number,
  created_at: string,
  ewScore: number
): Alert => ({
  id,
  title,
  description,
  level,
  category,
  status,
  created_at,
  updated_at: created_at,
  lat,
  lon,
  municipality,
  province,
  region,
  ew_score: ewScore,
});

export const ALERTS: Alert[] = [
  makeAlert('ALT001', 'Allerta Frana Critica', 'Score EW 8.7 – accelerazione InSAR +3.2 mm/settimana, piogge cumulate 72h superano soglia Tc3.', 'P4', 'landslide', 'active', 'Niscemi', 'Caltanissetta', 'Sicilia', 37.1498, 14.3896, '2026-03-08T06:30:00Z', 8.7),
  makeAlert('ALT002', 'Rischio Esondazione Elevato', 'Livello idrometrico Reno a 4.8 m (+1.2 m in 6h). Score EW 7.8 – soglia pre-allerta superata.', 'P3', 'flood', 'acknowledged', 'Argenta', 'Ferrara', 'Emilia-Romagna', 44.6134, 11.8296, '2026-03-08T05:45:00Z', 7.8),
  makeAlert('ALT003', 'Subsidenza Accelerata', 'Velocity InSAR -18 mm/anno, anomalia +40% rispetto baseline. Possibile cedimento infrastrutture.', 'P3', 'erosion', 'active', 'Modena', 'Modena', 'Emilia-Romagna', 44.6471, 10.9252, '2026-03-08T04:20:00Z', 7.2),
  makeAlert('ALT004', 'Accumulo Pluviometrico', 'Previsione 150 mm/24h su bacino Bisagno. Modello XGBoost Frana indica probabilità frana 68%.', 'P2', 'landslide', 'acknowledged', 'Genova', 'Genova', 'Liguria', 44.4056, 8.9463, '2026-03-08T03:10:00Z', 6.1),
  makeAlert('ALT005', 'Instabilità Versante', 'Rilevata deformazione progressiva 8 mm/mese sul versante NW. Attivazione possibile con piogge >30 mm.', 'P3', 'landslide', 'acknowledged', 'Ancona', 'Ancona', 'Marche', 43.6158, 13.5189, '2026-03-08T02:00:00Z', 6.4),
  makeAlert('ALT006', 'SWI Anomalia', 'Soil Water Index +0.31 sopra climatologia. Saturazione prossima a soglia critica per debris flow.', 'P2', 'landslide', 'acknowledged', 'Trento', 'Trento', 'Trentino-A.A.', 46.0748, 11.1217, '2026-03-07T22:15:00Z', 5.4),
  makeAlert('ALT007', 'Monitoraggio Ordinario', 'Score EW stabile a 2.1. Nessuna anomalia rilevata nei parametri di controllo.', 'P1', 'combined', 'resolved', 'Bologna', 'Bologna', 'Emilia-Romagna', 44.4949, 11.3426, '2026-03-07T18:00:00Z', 2.1),
];

// --------------------------------------------------
// Risk trend – 30 days (score 0-10, internal widget data)
// --------------------------------------------------
export interface RiskTrendPoint {
  date: string;
  score: number;
  level: AlertLevel;
}

function scoreToLevel(s: number): AlertLevel {
  if (s >= 8.5) return 'P4';
  if (s >= 7.0) return 'P3';
  if (s >= 5.0) return 'P2';
  return 'P1';
}

export const RISK_TREND_30D: RiskTrendPoint[] = (() => {
  const base = new Date('2026-02-06');
  const scores = [
    3.2, 3.5, 3.1, 3.8, 4.2, 4.0, 3.7, 3.9, 4.5, 4.8,
    5.1, 5.4, 5.0, 4.7, 5.8, 6.2, 5.9, 6.5, 7.0, 7.4,
    7.1, 6.8, 7.5, 7.9, 8.1, 7.8, 8.3, 8.5, 8.6, 8.7,
  ];
  return scores.map((score, i) => {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    return { date: d.toISOString().slice(0, 10), score, level: scoreToLevel(score) };
  });
})();

// --------------------------------------------------
// Displacement – Niscemi, 12-day Sentinel-1 intervals
// --------------------------------------------------
export interface DisplacementPoint {
  date: string;
  cumulative: number; // mm
  velocity: number;   // mm/yr
}

export const DISPLACEMENT_DATA: DisplacementPoint[] = [
  { date: '2025-06-01', cumulative: 0, velocity: -5.2 },
  { date: '2025-06-13', cumulative: -1.7, velocity: -5.8 },
  { date: '2025-06-25', cumulative: -3.0, velocity: -6.2 },
  { date: '2025-07-07', cumulative: -4.5, velocity: -7.1 },
  { date: '2025-07-19', cumulative: -6.2, velocity: -7.8 },
  { date: '2025-07-31', cumulative: -7.8, velocity: -8.4 },
  { date: '2025-08-12', cumulative: -9.1, velocity: -9.0 },
  { date: '2025-08-24', cumulative: -10.6, velocity: -9.6 },
  { date: '2025-09-05', cumulative: -12.4, velocity: -10.5 },
  { date: '2025-09-17', cumulative: -13.9, velocity: -11.2 },
  { date: '2025-09-29', cumulative: -15.3, velocity: -12.0 },
  { date: '2025-10-11', cumulative: -16.8, velocity: -13.1 },
  { date: '2025-10-23', cumulative: -18.5, velocity: -14.4 },
  { date: '2025-11-04', cumulative: -19.9, velocity: -15.2 },
  { date: '2025-11-16', cumulative: -21.4, velocity: -16.8 },
  { date: '2025-11-28', cumulative: -22.8, velocity: -17.9 },
  { date: '2025-12-10', cumulative: -23.6, velocity: -19.1 },
  { date: '2025-12-22', cumulative: -24.3, velocity: -20.5 },
  { date: '2026-01-03', cumulative: -24.9, velocity: -21.3 },
  { date: '2026-01-15', cumulative: -25.6, velocity: -22.8 },
  { date: '2026-01-27', cumulative: -26.2, velocity: -23.4 },
  { date: '2026-02-08', cumulative: -26.8, velocity: -24.1 },
  { date: '2026-02-20', cumulative: -27.5, velocity: -24.9 },
  { date: '2026-03-04', cumulative: -28.3, velocity: -25.6 },
];

// --------------------------------------------------
// Rainfall
// --------------------------------------------------
export interface RainfallPoint {
  date: string;
  value: number;
  cumulative30d: number;
}

export const RAINFALL_DATA: RainfallPoint[] = (() => {
  const start = new Date('2025-09-01');
  const daily = [
    8, 12, 5, 0, 18, 22, 35, 15, 4, 0, 0, 2, 8, 45, 38, 20, 6, 0, 0, 12,
    28, 18, 5, 0, 0, 0, 15, 42, 31, 18, 9, 2, 0, 0, 8, 55, 48, 25, 12, 4,
    0, 0, 3, 18, 32, 22, 8, 0, 0, 0, 12, 38, 65, 48, 28, 14, 5, 2, 0, 0,
    8, 22, 18, 5, 0, 0, 12, 35, 28, 15, 6, 0, 0, 0, 5, 18, 28, 22, 8, 2,
    0, 0, 8, 42, 58, 72, 38, 18, 8, 2, 0, 0, 5, 25, 45, 62, 55, 30, 12, 4,
    0, 0, 2, 15, 28, 48, 72, 85, 62, 38, 18, 8, 2, 0,
    68, 72, 85, 62, 38, 18, 8, 2, 0, 0, 5, 15, 28, 42, 35, 18, 8, 2, 0, 0,
    0, 8, 22, 42, 38, 22, 8, 2, 0, 0, 2, 15, 32, 48, 55, 45, 25, 12, 4, 0,
    0, 2, 18, 35, 58, 72, 65, 42, 22, 8, 2, 0, 0, 5, 22, 38, 52, 62, 48, 25,
    12, 4, 0, 0, 8, 28, 45, 62, 82, 95, 68, 42, 18, 6, 2, 0,
  ];
  const points: RainfallPoint[] = [];
  for (let i = 0; i < daily.length; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    // True 30-day rolling sum
    let sum30 = 0;
    for (let j = Math.max(0, i - 29); j <= i; j++) sum30 += daily[j];
    points.push({ date: d.toISOString().slice(0, 10), value: daily[i], cumulative30d: sum30 });
  }
  return points;
})();

// --------------------------------------------------
// SWI
// --------------------------------------------------
export interface SWIPoint {
  date: string;
  value: number;
  anomaly: number;
}

export const SWI_DATA: SWIPoint[] = (() => {
  // Realistic SWI: responds to rainfall with inertia (rises fast, decays slow)
  const points: SWIPoint[] = [];
  let swi = 0.25; // start dry
  const climatology = (i: number) => 0.45 + 0.15 * Math.sin((i - 40) * 2 * Math.PI / 180); // seasonal baseline
  for (let i = 0; i < RAINFALL_DATA.length; i++) {
    const rain = RAINFALL_DATA[i].value;
    // Wetting: +0.015 per mm of rain (fast response)
    const wetting = rain * 0.012;
    // Drying: decay toward 0.15 (slow, ~2% per day)
    const drying = (swi - 0.15) * 0.025;
    swi = Math.min(0.98, Math.max(0.10, swi + wetting - drying));
    const clim = climatology(i);
    const anomaly = swi - clim;
    points.push({
      date: RAINFALL_DATA[i].date,
      value: parseFloat(swi.toFixed(3)),
      anomaly: parseFloat(anomaly.toFixed(3)),
    });
  }
  return points;
})();

// --------------------------------------------------
// ML Models – aligned with ModelInfo interface
// --------------------------------------------------
export const ML_MODELS: ModelInfo[] = [
  {
    id: 'MDL001',
    name: 'XGBoost Frana',
    version: '1.0.2',
    description: 'Modello baseline suscettibilità frana su feature stack 14 bande (slope, TWI, curvature, litologia, etc.). Spatial cross-validation su IFFI.',
    status: 'active' as ModelStatus,
    category: 'landslide' as RiskCategory,
    algorithm: 'XGBoost + Spatial CV',
    auc_roc: 0.934,
    precision: 0.881,
    recall: 0.892,
    f1_score: 0.886,
    training_date: '2026-02-15',
    last_inference: '2026-03-08T06:00:00Z',
    inference_count_24h: 1247,
    data_sources: ['lidar', 'iffi', 'sentinel', 'arpa', 'egms', 'clc'],
    coverage_regions: ['Italia'],
  },
  {
    id: 'MDL002',
    name: 'XGBoost Alluvione',
    version: '1.0.1',
    description: 'Modello suscettibilità alluvione. Pesi EW calibrati: rain 0.40, SWI 0.35, susc 0.20, InSAR 0.05. Validato su retrocast Emilia-Romagna 2023.',
    status: 'active' as ModelStatus,
    category: 'flood' as RiskCategory,
    algorithm: 'XGBoost + Spatial CV',
    auc_roc: 0.912,
    precision: 0.878,
    recall: 0.891,
    f1_score: 0.884,
    training_date: '2026-02-20',
    last_inference: '2026-03-08T05:30:00Z',
    inference_count_24h: 756,
    data_sources: ['lidar', 'arpa', 'swi', 'sentinel', 'clc'],
    coverage_regions: ['Emilia-Romagna', 'Lombardia', 'Veneto'],
  },
  {
    id: 'MDL003',
    name: 'Prithvi-EO-2.0',
    version: '2.0.0',
    description: 'IBM/NASA geospatial foundation model. Fine-tuning pianificato su IFFI + LiDAR 1m per Italia (Livello 2-3).',
    status: 'training' as ModelStatus,
    category: 'combined' as RiskCategory,
    algorithm: 'Vision Transformer (ViT) + MAE',
    auc_roc: 0.958,
    precision: 0.911,
    recall: 0.927,
    f1_score: 0.919,
    training_date: '2026-03-01',
    last_inference: '2026-03-07T00:00:00Z',
    inference_count_24h: 0,
    data_sources: ['sentinel', 'lidar', 'egms'],
    coverage_regions: ['Italia', 'Europa'],
  },
  {
    id: 'MDL004',
    name: 'InSAR-LSTM Velocity',
    version: '0.9.1-beta',
    description: 'LSTM multi-variate per predizione accelerazione InSAR. Fine-tuning su serie temporali Sentinel-1C/1D + EGMS.',
    status: 'training' as ModelStatus,
    category: 'erosion' as RiskCategory,
    algorithm: 'LSTM + Attention',
    auc_roc: 0.889,
    precision: 0.842,
    recall: 0.868,
    f1_score: 0.855,
    training_date: '2025-12-01',
    last_inference: '2026-03-07T00:00:00Z',
    inference_count_24h: 0,
    data_sources: ['insar', 'egms', 'sentinel'],
    coverage_regions: ['Italia'],
  },
];

// --------------------------------------------------
// Hero stats (used by StatCard row on Dashboard)
// --------------------------------------------------
export const HERO_STATS = {
  monitoredAreas: 47,
  avgEwScore: 4.8,
  activeAlerts: 3,
  mlModels: 5,
} as const;
